import './index.scss';

import { Component, createElement, HTMLAttributes, Ref } from 'react';

import { BraxThemeVariant } from '../../types';

import arrow from '../../assets/icons/arrow.svg';
import bidirectionalArrow from '../../assets/icons/bidirectional-arrow.svg';
import arrowExpand from '../../assets/icons/arrow-expand.svg';
import arrowCollapse from '../../assets/icons/arrow-collapse.svg';
import invoice from '../../assets/icons/invoice.svg';
import picture from '../../assets/icons/picture.svg';
import blur from '../../assets/icons/blur.svg';
import video from '../../assets/icons/video.svg';
import music from '../../assets/icons/music.svg';
import file from '../../assets/icons/file.svg';
import newFile from '../../assets/icons/file.svg';
import folderOpened from '../../assets/icons/folder-opened.svg';
import newFolder from '../../assets/icons/new-folder.svg';
import mail from '../../assets/icons/mail.svg';
import upload from '../../assets/icons/upload.svg';
import download from '../../assets/icons/download.svg';
import cloudSync from '../../assets/icons/cloud-sync.svg';
import sync from '../../assets/icons/sync.svg';
import clone from '../../assets/icons/clone.svg';
import copy from '../../assets/icons/copy.svg';
import play from '../../assets/icons/play.svg';
import stop from '../../assets/icons/stop.svg';
import info from '../../assets/icons/info.svg';
import filter from '../../assets/icons/filter.svg';
import plusRounded from '../../assets/icons/plus-rounded.svg';
import close from '../../assets/icons/close.svg';
import threeDots from '../../assets/icons/three-dots.svg';
import sixDots from '../../assets/icons/six-dots.svg';
import bars from '../../assets/icons/bars.svg';
import grid from '../../assets/icons/grid.svg';
import list from '../../assets/icons/list.svg';
import hand from '../../assets/icons/hand.svg';
import eye from '../../assets/icons/eye.svg';
import eyeSlashed from '../../assets/icons/eye-slashed.svg';
import clock from '../../assets/icons/clock.svg';
import calendar from '../../assets/icons/calendar.svg';
import gear from '../../assets/icons/gear.svg';
import card from '../../assets/icons/card.svg';
import cardLocked from '../../assets/icons/card-locked.svg';
import pencil from '../../assets/icons/pencil.svg';
import marker from '../../assets/icons/marker.svg';
import navigation from '../../assets/icons/navigation.svg';
import map from '../../assets/icons/map.svg';
import floorPlan from '../../assets/icons/floor-plan.svg';
import book from '../../assets/icons/book.svg';
import magnifier from '../../assets/icons/magnifier.svg';
import trash from '../../assets/icons/trash.svg';
import lock from '../../assets/icons/lock.svg';
import chain from '../../assets/icons/chain.svg';
import bell from '../../assets/icons/bell.svg';
import crown from '../../assets/icons/crown.svg';
import office from '../../assets/icons/office.svg';
import check from '../../assets/icons/check.svg';
import checkRounded from '../../assets/icons/check-rounded.svg';
import exclamationMark from '../../assets/icons/exclamation-mark.svg';
import exclamationMarkTriangle from '../../assets/icons/exclamation-mark-triangle.svg';
import questionMark from '../../assets/icons/question-mark.svg';
import tooltip from '../../assets/icons/tooltip.svg';
import text from '../../assets/icons/text.svg';
import alignLeft from '../../assets/icons/align-left.svg';
import alignCenter from '../../assets/icons/align-center.svg';
import alignRight from '../../assets/icons/align-right.svg';
import alignJustify from '../../assets/icons/align-justify.svg';
import alphabetAZ from '../../assets/icons/alphabet-az.svg';
import alphabetZA from '../../assets/icons/alphabet-za.svg';
import money from '../../assets/icons/money.svg';
import _360 from '../../assets/icons/360.svg';
import chart from '../../assets/icons/chart.svg';
import graph from '../../assets/icons/graph.svg';
import user from '../../assets/icons/user.svg';
import newUser from '../../assets/icons/new-user.svg';
import facebook from '../../assets/icons/facebook.svg';
import whatsapp from '../../assets/icons/whatsapp.svg';
import google from '../../assets/icons/google.svg';

const icons = {
    'arrow': arrow,
    'bidirectional-arrow': bidirectionalArrow,
    'arrow-expand': arrowExpand,
    'arrow-collapse': arrowCollapse,
    'invoice': invoice,
    'picture': picture,
    'blur': blur,
    'video': video,
    'music': music,
    'file': file,
    'new-file': newFile,
    'folder-opened': folderOpened,
    'new-folder': newFolder,
    'mail': mail,
    'upload': upload,
    'download': download,
    'cloud-sync': cloudSync,
    'sync': sync,
    'clone': clone,
    'copy': copy,
    'play': play,
    'stop': stop,
    'info': info,
    'filter': filter,
    'plus-rounded': plusRounded,
    'close': close,
    'three-dots': threeDots,
    'six-dots': sixDots,
    'bars': bars,
    'grid': grid,
    'list': list,
    'hand': hand,
    'eye': eye,
    'eye-slashed': eyeSlashed,
    'clock': clock,
    'calendar': calendar,
    'gear': gear,
    'card': card,
    'card-locked': cardLocked,
    'pencil': pencil,
    'marker': marker,
    'navigation': navigation,
    'map': map,
    'floor-plan': floorPlan,
    'book': book,
    'magnifier': magnifier,
    'trash': trash,
    'lock': lock,
    'chain': chain,
    'bell': bell,
    'crown': crown,
    'office': office,
    'check': check,
    'check-rounded': checkRounded,
    'exclamation-mark': exclamationMark,
    'exclamation-mark-triangle': exclamationMarkTriangle,
    'question-mark': questionMark,
    'tooltip': tooltip,
    'text': text,
    'align-left': alignLeft,
    'align-center': alignCenter,
    'align-right': alignRight,
    'align-justify': alignJustify,
    'alphabet-az': alphabetAZ,
    'alphabet-za': alphabetZA,
    'money': money,
    '360': _360,
    'chart': chart,
    'graph': graph,
    'user': user,
    'new-user': newUser,
    'facebook': facebook,
    'whatsapp': whatsapp,
    'google': google
};

export type BraxIconName = (keyof typeof icons) | (string & {});

export type BraxIconProps<I extends BraxIconName> = HTMLAttributes<HTMLElement> & {
    innerRef?: Ref<HTMLSpanElement>,
    icon: I,
    variant?: BraxThemeVariant,
    rotation: number
};

export class BraxIcon<I extends BraxIconName> extends Component<BraxIconProps<I>> {

    static defaultProps = {
        rotation: 0
    };

    render() {
        const { innerRef, icon, variant, rotation, className, style, ...props } = this.props;

        return (
            <span
                ref={innerRef}
                className={`brax-icon ${variant || ''} ${className || ''}`}
                style={{ '--rotation': `${rotation}deg`, ...(style || {}) } as any}
                {...props}
            >
                { icon in icons ? createElement(icons[icon as BraxIconName]) : icon }
            </span>
        );
    }

}
